'use client';

import { useState } from 'react';
import { Search } from 'lucide-react';
import { useMountEffect } from '~/utils/react';
import { cn } from '~/utils';
import Icon from './Icon';

const TYPE_CHAR_MS = 55;

type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'placeholder'> & {
    ref?: React.Ref<HTMLInputElement>;
    value: string;
    /** Types itself in once on mount, then stays as a plain placeholder (also after clearing). */
    placeholder: string;
    /** Clears the field; renders a clear button while it has a value. */
    onClear?: () => void;
    clearLabel: string;
    /** Font size and weight, shared by the input and the typed placeholder so they line up. */
    textClassName: string;
    iconClassName?: string;
    /** Wrapper around the text: auto-width (headline) by default, `flex-1` to fill a bar. */
    fieldClassName?: string;
};

/** Search field on a dark surface: a search icon, then a placeholder that types itself in so the
 * empty field shows where typing starts. Used by the PLP hero title and the FAQ search. */
export const TypedSearchInput: React.FC<Props> = ({
    ref,
    value,
    placeholder,
    onClear,
    clearLabel,
    textClassName,
    iconClassName,
    fieldClassName,
    className,
    style,
    ...inputProps
}) => {
    // Once typed in, or once the visitor types, the placeholder stays static and the native
    // (blinking) caret takes over: clearing the field must not replay the intro.
    const [isIntroDone, setIntroDone] = useState(false);
    const isEmpty = value.length === 0;

    return (
        <div className={cn('flex min-w-0 max-w-full items-center gap-3', className)}>
            <Search
                className={cn('shrink-0 text-white/60', iconClassName)}
                strokeWidth={2.5}
                aria-hidden
            />
            {/* One grid cell holds the sizer, the input and the placeholder, so the field is as wide as
            its text and the placeholder sits exactly where typed text starts. */}
            <div
                className={cn(
                    'grid min-w-0 max-w-full overflow-hidden [&>*]:col-start-1 [&>*]:row-start-1',
                    textClassName,
                    fieldClassName,
                )}
                style={style}
            >
                <span aria-hidden className="invisible whitespace-pre">
                    {value || placeholder}&nbsp;
                </span>
                <input
                    type="text"
                    // Drop the default ~20ch intrinsic width so the sizer alone decides the field width.
                    size={1}
                    aria-label={placeholder}
                    {...inputProps}
                    onChange={e => {
                        setIntroDone(true);
                        inputProps.onChange?.(e);
                    }}
                    ref={ref}
                    value={value}
                    className={cn(
                        'w-full min-w-0 border-none bg-transparent p-0 text-white outline-none ring-0 focus:ring-0',
                        // While the intro types, its own caret trails the text instead.
                        isEmpty && !isIntroDone && 'caret-transparent',
                    )}
                    style={{ font: 'inherit', lineHeight: 'inherit' }}
                />
                {isEmpty &&
                    (isIntroDone ? (
                        <span
                            aria-hidden
                            className="pointer-events-none whitespace-pre text-white/40"
                        >
                            {placeholder}
                        </span>
                    ) : (
                        <TypedPlaceholder text={placeholder} onDone={() => setIntroDone(true)} />
                    ))}
            </div>
            {onClear && !isEmpty && (
                <button
                    type="button"
                    onClick={onClear}
                    className="shrink-0 rounded-full bg-white/20 p-2 transition-colors hover:bg-white/30"
                    aria-label={clearLabel}
                >
                    <Icon name="close" className="text-white" size="xs" />
                </button>
            )}
        </div>
    );
};

const TypedPlaceholder: React.FC<{ text: string; onDone: () => void }> = ({ text, onDone }) => {
    const [length, setLength] = useState(0);
    useMountEffect(() => {
        let typed = 0;
        const id = setInterval(() => {
            typed += 1;
            setLength(typed);
            if (typed < text.length) return;
            clearInterval(id);
            onDone();
        }, TYPE_CHAR_MS);
        return () => clearInterval(id);
    });

    return (
        <span
            aria-hidden
            className="pointer-events-none flex items-center whitespace-pre text-white/40"
        >
            {text.slice(0, length)}
            {/* Solid while typing, like a real caret mid-keystroke; removed once typed. */}
            <span className="ml-0.5 inline-block h-[0.9em] w-[0.08em] min-w-[2px] rounded-full bg-white" />
        </span>
    );
};
